import { useCallback, useMemo } from 'react';

import { useGetUsersInfinite } from '@vlabs/api/artemy/client';
import type { UserRecord, UsersList } from '@vlabs/api/artemy/model';
import {
  mergeUsers,
  MIN_USER_SEARCH_LENGTH,
} from '@vlabs/monokit/shared/lib/search-users';

const DEFAULT_USERS_PAGE_SIZE = 10;

interface UseAttachDepartmentUsersOptionsParams {
  isOpen: boolean;
  query: string;
}

interface UseAttachDepartmentUsersOptionsReturn {
  fetchNextPage: () => void;
  hasError: boolean;
  hasMore: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  users: UserRecord[];
}

function getUsersFromPages(pages?: UsersList[]): UserRecord[] {
  return pages?.flatMap(({ users }) => users) ?? [];
}

function getNextPageParam(lastPage: UsersList, pages: UsersList[]): number | undefined {
  return lastPage.users.length === DEFAULT_USERS_PAGE_SIZE ? pages.length + 1 : undefined;
}

function useAttachDepartmentUsersOptions({
  isOpen,
  query,
}: UseAttachDepartmentUsersOptionsParams): UseAttachDepartmentUsersOptionsReturn {
  const trimmedQuery = query.trim();
  const isSearchMode = trimmedQuery.length >= MIN_USER_SEARCH_LENGTH;

  // без поиска показываем обычный постраничный список пользователей
  const allUsers = useGetUsersInfinite(undefined, {
    query: {
      enabled: isOpen && !isSearchMode,
      getNextPageParam,
      initialPageParam: 1,
    },
  });

  // поиск идет двумя like-запросами
  const usersByEmail = useGetUsersInfinite(
    {
      email: trimmedQuery.toLowerCase(),
    },
    {
      query: {
        enabled: isOpen && isSearchMode,
        getNextPageParam,
        initialPageParam: 1,
      },
    },
  );

  const usersByFullName = useGetUsersInfinite(
    {
      full_name: trimmedQuery,
    },
    {
      query: {
        enabled: isOpen && isSearchMode,
        getNextPageParam,
        initialPageParam: 1,
      },
    },
  );

  const foundUsers = useMemo(() => mergeUsers(
    getUsersFromPages(usersByEmail.data?.pages),
    getUsersFromPages(usersByFullName.data?.pages),
  ), [
    usersByEmail.data?.pages,
    usersByFullName.data?.pages,
  ]);
  const searchQueries = [usersByEmail, usersByFullName];
  const hasSearchError = searchQueries.some(({ isError }) => isError);
  const hasSearchMore = searchQueries.some(({ hasNextPage }) => hasNextPage);
  const isSearchFetchingNextPage = searchQueries.some(({ isFetchingNextPage }) => isFetchingNextPage);
  const isSearchLoading = searchQueries.some(({ isLoading }) => isLoading);

  const fetchNextPage = useCallback((): void => {
    // для поиска нужно дозагрузить обе выдачи: по email и по full_name
    if (!isSearchMode) {
      if (allUsers.hasNextPage && !allUsers.isFetchingNextPage) {
        allUsers.fetchNextPage();
      }

      return;
    }

    if (usersByEmail.hasNextPage && !usersByEmail.isFetchingNextPage) {
      usersByEmail.fetchNextPage();
    }

    if (usersByFullName.hasNextPage && !usersByFullName.isFetchingNextPage) {
      usersByFullName.fetchNextPage();
    }
  }, [
    allUsers,
    isSearchMode,
    usersByEmail,
    usersByFullName,
  ]);

  if (!isSearchMode) {
    return {
      fetchNextPage,
      hasError: allUsers.isError,
      hasMore: allUsers.hasNextPage,
      isFetchingNextPage: allUsers.isFetchingNextPage,
      isLoading: allUsers.isLoading,
      users: getUsersFromPages(allUsers.data?.pages),
    };
  }

  return {
    fetchNextPage,
    hasError: hasSearchError,
    hasMore: hasSearchMore,
    isFetchingNextPage: isSearchFetchingNextPage,
    isLoading: isSearchLoading,
    users: foundUsers,
  };
}

export { useAttachDepartmentUsersOptions };
